/*
  Glimot product page behaviour (loaded by sections/main-product.liquid).
  Works alongside Craft's product-info.js / product-form.js / media-gallery.js without replacing them.

  1. Gallery previous/next on the main image.
  2. <glimot-info-area>: tabs | flow | disclosures, per breakpoint.
  3. "Require shoppers to choose options": nothing pre-selected until the shopper chooses
     (store default + custom.option_selection override, rendered as data-g-require). Without
     JavaScript Craft's normal pre-selected picker remains, so the product is still buyable.
  4. Sold-out / unavailable option messages.
  5. Personalisation: live counters, validation beside each field before adding, empty optional
     fields not sent, and privacy (never stored; cleared when the page is left).
  6. "Change options" edit mode (?g_edit=<line key>): add the new option first, then remove the
     old line; if adding fails the original line is untouched.
  7. Delivery & returns block kept in step with the chosen option.
  8. <glimot-sticky-bar>: mobile purchase bar mirroring the real Add to cart button.
  9. Recently viewed: product id/handle/time only, on this device (no personal data).
*/
(() => {
  if (window.GlimotProduct && typeof window.GlimotProduct.init === 'function') return;
  const strings = (window.glimot && window.glimot.strings) || {};
  const params = new URLSearchParams(window.location.search);
  const RECENT_KEY = 'glimot.recent';

  const fmt = (template, values) =>
    Object.keys(values).reduce((text, key) => text.split(`[${key}]`).join(values[key]), template || '');

  /* ---------- 1. Gallery arrows ---------- */
  function initGallery(root) {
    root.querySelectorAll('media-gallery').forEach((gallery) => {
      const nav = gallery.querySelector('[data-g-gallery-nav]');
      if (!nav || nav.dataset.ready) return;
      nav.dataset.ready = 'true';
      const step = (direction) => {
        const items = [...gallery.querySelectorAll('[id^="GalleryViewer"] [data-media-id]')];
        if (!items.length) return;
        const list = gallery.querySelector('[id^="Slider-Gallery"]');
        const isMobileSlider = window.matchMedia('(max-width: 749px)').matches && list && list.scrollWidth > list.clientWidth;
        let index;
        if (isMobileSlider) {
          // On phones the gallery is a swipe row: work out the current slide from the scroll position.
          index = items.reduce(
            (best, item, i) =>
              Math.abs(item.offsetLeft - list.scrollLeft) < Math.abs(items[best].offsetLeft - list.scrollLeft) ? i : best,
            0
          );
        } else {
          index = Math.max(0, items.findIndex((item) => item.classList.contains('is-active')));
        }
        const next = items[(index + direction + items.length) % items.length];
        if (isMobileSlider) {
          const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          list.scrollTo({ left: next.offsetLeft - items[0].offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
        } else if (typeof gallery.setActiveMedia === 'function') {
          gallery.setActiveMedia(next.dataset.mediaId, false);
        }
      };
      nav.querySelector('[data-g-gallery-prev]')?.addEventListener('click', () => step(-1));
      nav.querySelector('[data-g-gallery-next]')?.addEventListener('click', () => step(1));
    });
  }

  /* ---------- 2. Information area ---------- */
  class GlimotInfoArea extends HTMLElement {
    connectedCallback() {
      this.mq = window.matchMedia('(min-width: 750px)');
      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = [...this.querySelectorAll('.g-info__tab')];
      this.sections = [...this.querySelectorAll('.g-info__section')];
      this.multiple = this.dataset.multiple !== 'false';
      this.onMedia = () => this.apply();
      this.mq.addEventListener('change', this.onMedia);
      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.selectTab(index, true));
        tab.addEventListener('keydown', (event) => this.onTabKey(event, index));
      });
      this.sections.forEach((section) => {
        section.querySelector('.g-info__toggle')?.addEventListener('click', () => this.toggleSection(section));
      });
      this.apply();
    }

    disconnectedCallback() {
      this.mq?.removeEventListener('change', this.onMedia);
    }

    apply() {
      const mode = (this.mq.matches ? this.dataset.desktop : this.dataset.mobile) || 'flow';
      this.dataset.mode = mode;
      this.tablist.hidden = mode !== 'tabs';
      if (mode === 'tabs') {
        const active = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));
        this.sections.forEach((section) => {
          const panel = section.querySelector('.g-info__panel');
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', this.tabs[this.sections.indexOf(section)].id);
          panel.setAttribute('tabindex', '0');
        });
        this.selectTab(active, false);
      } else {
        this.sections.forEach((section) => {
          const panel = section.querySelector('.g-info__panel');
          panel.removeAttribute('role');
          panel.removeAttribute('tabindex');
          panel.removeAttribute('aria-labelledby');
          section.hidden = false;
          if (mode === 'disclosures') {
            this.setOpen(section, section.dataset.open === 'true', false);
          } else {
            panel.hidden = false;
          }
        });
      }
    }

    selectTab(index, focus) {
      this.tabs.forEach((tab, i) => {
        const selected = i === index;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
        this.sections[i].hidden = !selected;
        this.sections[i].querySelector('.g-info__panel').hidden = !selected;
      });
      if (focus) this.tabs[index]?.focus();
    }

    onTabKey(event, index) {
      const last = this.tabs.length - 1;
      let next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index === last ? 0 : index + 1;
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index === 0 ? last : index - 1;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = last;
      if (next === null) return;
      event.preventDefault();
      this.selectTab(next, true);
    }

    setOpen(section, open, remember = true) {
      const toggle = section.querySelector('.g-info__toggle');
      const panel = section.querySelector('.g-info__panel');
      toggle?.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      section.classList.toggle('is-open', open);
      if (remember) section.dataset.open = String(open);
    }

    toggleSection(section) {
      const open = section.dataset.open !== 'true';
      if (open && !this.multiple) this.sections.forEach((other) => other !== section && this.setOpen(other, false));
      this.setOpen(section, open);
    }
  }
  if (!customElements.get('glimot-info-area')) customElements.define('glimot-info-area', GlimotInfoArea);

  /* ---------- 3. Require option choice ---------- */
  function readJson(node) {
    try {
      return JSON.parse(node?.textContent || 'null');
    } catch (e) {
      return null;
    }
  }

  function initRequireChoice(productInfo) {
    const selects = productInfo.querySelector('variant-selects[data-g-require="true"]');
    const section = productInfo.dataset.section;
    if (!selects || params.has('variant') || params.has('g_edit') || productInfo.dataset.gRequireReady) return;
    productInfo.dataset.gRequireReady = 'true';
    const variants = readJson(selects.querySelector('[data-g-variants]')) || [];
    const groups = [...selects.querySelectorAll('fieldset, .product-form__input--dropdown')];
    if (!groups.length) return;
    // An option with a single value (e.g. "Title: Mahogany box") is not a choice: keep it selected.
    const fixed = groups.map((group) => {
      const values = new Set(
        [...group.querySelectorAll('input[type="radio"], select option')].map((input) => input.value).filter(Boolean)
      );
      return values.size === 1 ? [...values][0] : null;
    });
    if (fixed.every(Boolean)) return;
    const state = { pending: true, chosen: fixed.slice() };
    productInfo.gRequireState = state;

    const optionName = (group) =>
      (
        group.querySelector('.g-opt__name')?.textContent ||
        group.querySelector('legend, label.form__label')?.childNodes[0]?.textContent ||
        ''
      ).replace(':', '').trim();

    // Clear the pre-selected state.
    groups.forEach((group, i) => {
      if (fixed[i]) return;
      group.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.checked = false;
      });
      const select = group.querySelector('select');
      if (select) {
        [...select.options].forEach((option) => option.removeAttribute('selected'));
        const placeholder = document.createElement('option');
        placeholder.value = '';
        placeholder.disabled = true;
        placeholder.setAttribute('selected', 'selected');
        placeholder.dataset.gPlaceholder = 'true';
        placeholder.textContent = fmt(selects.dataset.gChooseTemplate || strings.chooseOption, { option: optionName(group).toLowerCase() });
        select.prepend(placeholder);
        select.value = '';
      }
      const selected = group.querySelector('[data-selected-value]');
      if (selected && !group.querySelector('.dropdown-swatch')) selected.textContent = '';
    });
    productInfo.querySelectorAll(`#product-form-${section} input[name="id"], #product-form-installment-${section} input[name="id"]`).forEach((input) => {
      input.value = '';
    });
    const priceNode = productInfo.querySelector(`#price-${section}`);
    const fromTemplate = productInfo.querySelector(`template[data-g-price-from="${section}"]`);
    if (priceNode && fromTemplate) priceNode.innerHTML = fromTemplate.innerHTML;
    [`#Sku-${section}`, `#Inventory-${section}`].forEach((sel) => productInfo.querySelector(sel)?.classList.add('hidden'));
    productInfo.querySelectorAll('[data-g-variant-estimate]').forEach((node) => (node.hidden = true));
    productInfo.querySelectorAll('[data-g-product-estimate]').forEach((node) => (node.hidden = false));
    const featured = selects.dataset.gFeaturedMedia;
    if (featured) productInfo.querySelector('media-gallery')?.setActiveMedia?.(`${section}-${featured}`, false);

    const updateButton = () => {
      const missing = groups.findIndex((group, i) => !state.chosen[i]);
      const button = productInfo.querySelector(`#ProductSubmitButton-${section}`);
      if (!button) return;
      if (missing >= 0) {
        button.setAttribute('disabled', 'disabled');
        button.setAttribute('aria-disabled', 'true');
        const label = fmt(selects.dataset.gChooseTemplate || strings.chooseOption, { option: optionName(groups[missing]).toLowerCase() });
        const span = button.querySelector('span');
        if (span) span.textContent = label;
        button.dataset.gMissingOption = String(missing);
      }
    };

    const markAvailability = () => {
      groups.forEach((group, position) => {
        group.querySelectorAll('input[type="radio"], select option:not([data-g-placeholder])').forEach((input) => {
          const value = input.value;
          const possible = variants.some(
            (variant) =>
              variant.available &&
              variant.options[position] === value &&
              state.chosen.every((chosen, i) => i === position || !chosen || variant.options[i] === chosen)
          );
          input.classList.toggle('disabled', !possible);
        });
      });
    };

    const showMedia = () => {
      const match = variants.find(
        (variant) => variant.media && state.chosen.every((chosen, i) => !chosen || variant.options[i] === chosen)
      );
      if (match) productInfo.querySelector('media-gallery')?.setActiveMedia?.(`${section}-${match.media}`, false);
    };

    updateButton();
    markAvailability();

    // Capture phase on product-info: runs before Craft's variant-selects listener.
    productInfo.addEventListener(
      'change',
      (event) => {
        if (!state.pending) return;
        const group = groups.find((g) => g.contains(event.target));
        if (!group) return;
        const index = groups.indexOf(group);
        const target = event.target;
        let value = target.value;
        if (target.tagName === 'SELECT') {
          [...target.options].forEach((option) => option.removeAttribute('selected'));
          target.selectedOptions[0]?.setAttribute('selected', 'selected');
          value = target.value;
        }
        state.chosen[index] = value || null;
        const selectedLabel = group.querySelector('[data-selected-value]');
        if (selectedLabel && target.type === 'radio') selectedLabel.textContent = value;
        const complete = state.chosen.every(Boolean);
        if (complete) {
          // Every option chosen: hand over to Craft, which asks Shopify for the exact variant.
          state.pending = false;
          productInfo.querySelector(`#GOptionMessage-${section}`)?.replaceChildren();
          const button = productInfo.querySelector(`#ProductSubmitButton-${section}`);
          button?.removeAttribute('aria-disabled');
          if (button) delete button.dataset.gMissingOption;
          return;
        }
        event.stopPropagation();
        markAvailability();
        showMedia();
        updateButton();
      },
      true
    );
  }

  /* ---------- 4. Option messages ---------- */
  function updateOptionMessage(productInfo, variant) {
    const section = productInfo.dataset.section;
    const message = productInfo.querySelector(`#GOptionMessage-${section}`);
    if (!message) return;
    const selects = productInfo.querySelector('variant-selects');
    const chosen = selects
      ? [...selects.querySelectorAll('fieldset input:checked, select option[selected]:not([data-g-placeholder])')].map((node) => node.value)
      : [];
    if (!variant) {
      message.textContent = selects?.dataset.gUnavailableText || '';
      return;
    }
    if (!variant.available) {
      const template = selects?.dataset.gSoldOutTemplate || '[value]';
      message.textContent = fmt(template, { value: chosen.join(' / ') || variant.title });
      return;
    }
    message.textContent = '';
  }

  /* ---------- 5. Personalisation ---------- */
  class GlimotPersonalisation extends HTMLElement {
    connectedCallback() {
      this.fields = [...this.querySelectorAll('[data-g-pers-field]')];
      this.fields.forEach((field) => {
        const input = field.querySelector('.g-pers__input');
        const counter = field.querySelector('.g-pers__count');
        if (input && counter) {
          const update = () => {
            counter.textContent = fmt(this.dataset.countTemplate, { count: input.value.length, max: counter.dataset.max });
          };
          input.addEventListener('input', update);
          update();
        }
        input?.addEventListener('input', () => this.clearError(field));
        input?.addEventListener('change', () => this.clearError(field));
      });
      if (params.get('g_reenter') === '1') {
        const note = this.querySelector('[data-g-reenter]');
        if (note) note.hidden = false;
      }
    }

    clearError(field) {
      const error = field.querySelector('.g-pers__error');
      const input = field.querySelector('.g-pers__input');
      if (error) {
        error.hidden = true;
        error.textContent = '';
      }
      input?.removeAttribute('aria-invalid');
    }

    // Returns the first invalid input, or null. Spaces-only text doesn't count.
    validate() {
      let firstInvalid = null;
      this.fields.forEach((field) => {
        const input = field.querySelector('.g-pers__input');
        if (!input) return;
        this.clearError(field);
        if (field.dataset.required !== 'true') return;
        if (input.value.trim() === '') {
          const template = field.dataset.kind === 'select' ? this.dataset.choiceTemplate : this.dataset.requiredTemplate;
          const error = field.querySelector('.g-pers__error');
          error.textContent = fmt(template, { label: field.dataset.label });
          error.hidden = false;
          input.setAttribute('aria-invalid', 'true');
          if (!firstInvalid) firstInvalid = input;
        }
      });
      return firstInvalid;
    }

    // Empty optional fields are not sent (so orders carry no blank entries).
    disableEmpty() {
      const disabled = [];
      this.fields.forEach((field) => {
        const input = field.querySelector('.g-pers__input');
        if (input && input.value.trim() === '') {
          input.disabled = true;
          disabled.push(input);
        }
      });
      return () => disabled.forEach((input) => (input.disabled = false));
    }

    clearAll() {
      this.fields.forEach((field) => {
        const input = field.querySelector('.g-pers__input');
        if (!input) return;
        input.value = '';
        input.dispatchEvent(new Event('input'));
      });
    }

    fill(properties) {
      if (!properties) return;
      this.fields.forEach((field) => {
        const input = field.querySelector('.g-pers__input');
        if (!input) return;
        const match = /^properties\[(.*)\]$/.exec(input.name);
        const key = match ? match[1] : null;
        if (key && Object.prototype.hasOwnProperty.call(properties, key)) {
          input.value = properties[key];
          input.dispatchEvent(new Event('input'));
        }
      });
    }
  }
  if (!customElements.get('glimot-personalisation')) customElements.define('glimot-personalisation', GlimotPersonalisation);

  // Privacy: never keep personalisation text after leaving the page (including back/forward cache).
  window.addEventListener('pagehide', () => {
    document.querySelectorAll('glimot-personalisation').forEach((element) => element.clearAll?.());
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) document.querySelectorAll('glimot-personalisation').forEach((element) => element.clearAll?.());
  });

  /* ---------- Submit guard (capture phase, before Craft's product-form) ---------- */
  function initSubmitGuard(productInfo) {
    if (productInfo.dataset.gGuard) return;
    productInfo.dataset.gGuard = 'true';
    const section = productInfo.dataset.section;
    productInfo.addEventListener(
      'submit',
      (event) => {
        const form = event.target;
        if (!form.matches(`#product-form-${section}`)) return;
        const idInput = form.querySelector('input[name="id"]');
        const pers = productInfo.querySelector('glimot-personalisation') || document.querySelector(`glimot-personalisation[data-form="product-form-${section}"]`);

        // Never submit without a complete, valid option.
        if (!idInput || !idInput.value) {
          event.preventDefault();
          event.stopPropagation();
          const firstGroup = productInfo.querySelector('variant-selects fieldset input, variant-selects select');
          firstGroup?.focus();
          return;
        }
        if (pers) {
          const invalid = pers.validate();
          if (invalid) {
            event.preventDefault();
            event.stopPropagation();
            invalid.focus();
            invalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
            return;
          }
        }
        if (productInfo.gEditKey) {
          event.preventDefault();
          event.stopPropagation();
          runEditUpdate(productInfo, form, pers);
          return;
        }
        if (pers) {
          const restore = pers.disableEmpty();
          window.setTimeout(restore, 0);
        }
      },
      true
    );
  }

  /* ---------- 6. Change options edit mode ---------- */
  async function initEditMode(productInfo) {
    const key = params.get('g_edit');
    if (!key || productInfo.dataset.gEdit) return;
    productInfo.dataset.gEdit = 'true';
    productInfo.gEditKey = key;
    const section = productInfo.dataset.section;
    const button = productInfo.querySelector(`#ProductSubmitButton-${section}`);
    const label = button?.querySelector('span');
    if (label) label.textContent = productInfo.dataset.gUpdateLabel || 'Update item in cart';
    const quantity = parseInt(params.get('g_qty'), 10);
    const qtyInput = productInfo.querySelector('.quantity__input');
    if (qtyInput && quantity > 0) qtyInput.value = quantity;
    // Refill personalisation from Shopify's cart (server), never from device storage.
    try {
      const cart = await fetch(`${window.routes?.cart_url || '/cart'}.js`).then((r) => r.json());
      const line = (cart.items || []).find((item) => item.key === key);
      if (line && line.properties) {
        productInfo.querySelector('glimot-personalisation')?.fill(line.properties);
      }
    } catch (e) {
      /* the shopper can still enter details manually */
    }
  }

  async function runEditUpdate(productInfo, form, pers) {
    const section = productInfo.dataset.section;
    const button = productInfo.querySelector(`#ProductSubmitButton-${section}`);
    const errorBox = productInfo.querySelector('.product-form__error-message-wrapper');
    const errorText = productInfo.querySelector('.product-form__error-message');
    // Submit the real product form as multipart FormData (like Craft's product form), so every field
    // that belongs to it — theme personalisation, vendor app fields, hidden properties and file
    // uploads — is sent unchanged. Empty optional theme fields are left out, as on a normal add.
    const restore = pers ? pers.disableEmpty() : () => {};
    const formData = new FormData(form);
    restore();
    button?.setAttribute('aria-busy', 'true');
    button?.classList.add('loading');
    const added = await window.Glimot.addFormData(formData);
    if (!added.ok) {
      button?.removeAttribute('aria-busy');
      button?.classList.remove('loading');
      if (errorBox && errorText) {
        errorBox.hidden = false;
        errorText.textContent = `${added.message || ''} ${productInfo.dataset.gUpdateFailed || ''}`.trim();
      }
      return;
    }
    const removed = await window.Glimot.changeLine(productInfo.gEditKey, 0);
    if (!removed.ok && errorBox && errorText) {
      errorBox.hidden = false;
      errorText.textContent = removed.message || '';
    }
    window.location.href = window.glimot?.routes?.cart || '/cart';
  }

  /* ---------- 7. Delivery block + estimates ---------- */
  function onVariantChange({ data }) {
    if (!data) return;
    const productInfo = document.querySelector(`product-info[data-section="${data.sectionId}"]`);
    if (!productInfo) return;
    const source = data.html?.getElementById(`GDelivery-${data.sectionId}`);
    const target = productInfo.querySelector(`#GDelivery-${data.sectionId}`);
    if (source && target) target.innerHTML = source.innerHTML;
    updateOptionMessage(productInfo, data.variant);
    // Craft resets the button text on every option change; keep the edit-mode wording.
    if (productInfo.gEditKey) {
      const button = productInfo.querySelector(`#ProductSubmitButton-${data.sectionId}`);
      const label = button?.querySelector('span');
      if (button && label && !button.disabled) label.textContent = productInfo.dataset.gUpdateLabel || label.textContent;
    }
    productInfo.querySelector('glimot-sticky-bar')?.sync?.();
  }

  /* ---------- 8. Sticky bar ---------- */
  class GlimotStickyBar extends HTMLElement {
    connectedCallback() {
      if (this.closest('quick-add-modal')) return;
      this.section = this.dataset.section;
      this.productInfo = this.closest('product-info');
      this.button = this.querySelector('[data-g-sticky-button]');
      this.label = this.querySelector('[data-g-sticky-label]');
      this.mq = window.matchMedia('(max-width: 749px)');
      this.mainVisible = true;
      this.observeMain();
      this.button.addEventListener('click', () => this.onClick());
      this.mq.addEventListener('change', () => this.update());
      window.visualViewport?.addEventListener('resize', () => this.update());
      document.addEventListener('focusin', () => this.update());
      document.addEventListener('focusout', () => window.setTimeout(() => this.update(), 50));
      new MutationObserver(() => this.update()).observe(document.body, { attributes: true, attributeFilter: ['class'] });
      this.sync();
    }

    get mainButton() {
      // The native Add to cart button, or the vendor-page "Personalise" link for vendor-page products.
      return (
        this.productInfo?.querySelector(`#ProductSubmitButton-${this.section}`) ||
        this.productInfo?.querySelector('[data-g-vendor-page]')
      );
    }

    // The native control is only pressed when it is really the active purchase action: present,
    // rendered and visible (a vendor app may hide it and use its own button), and enabled.
    isUsable(main) {
      if (!main || !main.isConnected) return false;
      if (!main.getClientRects().length) return false;
      const style = getComputedStyle(main);
      if (style.visibility === 'hidden' || style.display === 'none') return false;
      return !main.disabled && main.getAttribute('aria-disabled') !== 'true';
    }

    scrollToPurchase() {
      const target =
        this.productInfo.querySelector('.product-form, .g-vendor-action, [id^="shopify-block-"]') ||
        this.productInfo.querySelector('.product__info-container');
      target?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }

    observeMain() {
      const main = this.mainButton;
      if (!main) return;
      this.io?.disconnect();
      this.io = new IntersectionObserver((entries) => {
        // Shown only while the main button is off-screen, so the two are never visible together.
        this.mainVisible = entries.some((entry) => entry.isIntersecting);
        this.update();
      });
      this.io.observe(main);
      this.mo?.disconnect();
      this.mo = new MutationObserver(() => this.sync());
      this.mo.observe(main, { attributes: true, childList: true, subtree: true, characterData: true });
    }

    sync() {
      const main = this.mainButton;
      if (!main) return;
      if (!this.mo) this.observeMain();
      const text = (main.matches('a') ? main.textContent.trim() : main.querySelector('span')?.textContent?.trim()) || strings.addToCart;
      this.label.textContent = text;
      const disabled = main.hasAttribute('disabled') || main.getAttribute('aria-disabled') === 'true';
      const missingChoice = main.dataset.gMissingOption !== undefined && this.productInfo.gRequireState?.pending;
      // Disabled because a choice is missing: keep the bar usable so it can guide to the field.
      this.button.disabled = disabled && !missingChoice;
      this.button.classList.toggle('loading', main.classList.contains('loading'));
      this.button.setAttribute('aria-busy', String(main.classList.contains('loading')));
      const price = this.productInfo.querySelector(`#price-${this.section} .price`);
      const target = this.querySelector('[data-g-sticky-price]');
      if (price && target) {
        const sale = price.classList.contains('price--on-sale');
        const node = price.querySelector(sale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular');
        target.textContent = (node || price).textContent.trim().replace(/\s+/g, ' ');
      }
      const chosen = [...(this.productInfo.querySelectorAll('variant-selects fieldset input:checked, variant-selects select option[selected]:not([data-g-placeholder])') || [])]
        .map((node) => node.value)
        .join(' / ');
      this.querySelector('[data-g-sticky-variant]').textContent = chosen;
    }

    keyboardOpen() {
      const active = document.activeElement;
      const typing = active && (active.matches('input:not([type="radio"]):not([type="checkbox"]), textarea, select') && !this.contains(active));
      const vv = window.visualViewport;
      return typing || (vv && vv.height < window.innerHeight * 0.75);
    }

    overlayOpen() {
      return (
        document.body.classList.contains('overflow-hidden') ||
        document.body.classList.contains('overflow-hidden-tablet') ||
        document.getElementById('cart-notification')?.classList.contains('active')
      );
    }

    update() {
      const show = this.mq.matches && !this.mainVisible && !this.overlayOpen() && !this.keyboardOpen();
      if (show === !this.hidden) return;
      this.hidden = !show;
      document.body.classList.toggle('g-has-sticky-bar', show);
      const height = show ? this.offsetHeight : 0;
      document.documentElement.style.setProperty('--g-sticky-bar-height', `${height}px`);
    }

    onClick() {
      const main = this.mainButton;
      if (!main) return;
      if (main.matches('a[data-g-vendor-page]')) {
        // Vendor-hosted customization page: follow the merchant's link, nothing is added here.
        main.click();
        return;
      }
      const state = this.productInfo.gRequireState;
      if (state && state.pending) {
        const missing = Number(main.dataset.gMissingOption || 0);
        const group = this.productInfo.querySelectorAll('variant-selects fieldset, variant-selects .product-form__input--dropdown')[missing];
        group?.scrollIntoView({ block: 'center', behavior: 'smooth' });
        group?.querySelector('input, select')?.focus({ preventScroll: true });
        return;
      }
      const pers = this.productInfo.querySelector('glimot-personalisation');
      const invalid = pers?.validate();
      if (invalid) {
        invalid.scrollIntoView({ block: 'center', behavior: 'smooth' });
        invalid.focus({ preventScroll: true });
        return;
      }
      if (!this.isUsable(main)) {
        // Hidden (e.g. replaced by a vendor app's own button) or disabled: never press it.
        this.scrollToPurchase();
        return;
      }
      main.click();
    }
  }
  if (!customElements.get('glimot-sticky-bar')) customElements.define('glimot-sticky-bar', GlimotStickyBar);

  /* ---------- 9. Recently viewed (device only) ---------- */
  function recordRecentlyViewed(productInfo) {
    const id = productInfo.dataset.productId;
    const handle = productInfo.dataset.gHandle;
    if (!id || !handle || productInfo.closest('quick-add-modal')) return;
    // Merchant-classified add-on products are never recorded as recently viewed.
    if (productInfo.dataset.gAddon === 'true') return;
    try {
      const list = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]').filter((item) => String(item.id) !== String(id));
      list.unshift({ id: Number(id), handle, viewedAt: Date.now() });
      window.localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 16)));
    } catch (e) {
      /* storage unavailable */
    }
  }

  /* ---------- init ---------- */
  function init(productInfo) {
    initGallery(productInfo);
    initRequireChoice(productInfo);
    initSubmitGuard(productInfo);
    initEditMode(productInfo);
    recordRecentlyViewed(productInfo);
    productInfo.querySelector('glimot-sticky-bar')?.sync?.();
  }

  function initAll() {
    document.querySelectorAll('product-info').forEach(init);
  }

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, onVariantChange);
  }
  document.addEventListener('product-info:loaded', (event) => {
    const productInfo = event.target.closest?.('product-info') || event.target;
    if (productInfo && productInfo.tagName === 'PRODUCT-INFO') init(productInfo);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  window.GlimotProduct = { init };
})();

/* Option -/+ toggle (variant picker block "Let shoppers collapse each option"). The choices stay in
   the page and in the form when collapsed; only their display changes. Craft re-renders the picker on
   a variant change, which opens the options again. */
(() => {
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('.g-opt__toggle');
    if (!toggle) return;
    const values = document.getElementById(toggle.getAttribute('aria-controls'));
    if (!values) return;
    const open = toggle.getAttribute('aria-expanded') !== 'false';
    toggle.setAttribute('aria-expanded', String(!open));
    values.hidden = open;
  });
})();
